import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect } from 'react';
import { cn } from '../utils/cn';
import { X } from 'lucide-react';
const DialogContext = React.createContext(null);
const Dialog = ({ open = false, onOpenChange, children }) => {
    const [internalOpen, setInternalOpen] = useState(open);
    const [animating, setAnimating] = useState(false);
    const handleOpenChange = useCallback((newOpen) => {
        if (newOpen) {
            setInternalOpen(true);
            requestAnimationFrame(() => setAnimating(true));
        }
        else {
            setAnimating(false);
            setTimeout(() => setInternalOpen(false), 200);
        }
        onOpenChange?.(newOpen);
    }, [onOpenChange]);
    useEffect(() => {
        if (open) {
            setInternalOpen(true);
            requestAnimationFrame(() => setAnimating(true));
        }
        else {
            setAnimating(false);
            setTimeout(() => setInternalOpen(false), 200);
        }
    }, [open]);
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && internalOpen)
                handleOpenChange(false);
        };
        if (internalOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => { document.removeEventListener('keydown', handleEscape); document.body.style.overflow = 'unset'; };
    }, [internalOpen, handleOpenChange]);
    if (!internalOpen && !animating)
        return _jsx(_Fragment, { children: children });
    return (_jsx(DialogContext.Provider, { value: { open: internalOpen, onOpenChange: handleOpenChange }, children: children }));
};
const DialogTrigger = ({ children, onClick }) => {
    const context = React.useContext(DialogContext);
    const handleClick = () => { onClick?.(); context?.onOpenChange(true); };
    if (React.isValidElement(children)) {
        return React.cloneElement(children, { onClick: handleClick });
    }
    return _jsx("button", { onClick: handleClick, children: children });
};
const DialogContent = ({ className, children }) => {
    const context = React.useContext(DialogContext);
    if (!context?.open)
        return null;
    const [show, setShow] = useState(false);
    useEffect(() => { requestAnimationFrame(() => setShow(true)); }, []);
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: [_jsx("div", { className: cn('absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200', show ? 'opacity-100' : 'opacity-0'), onClick: () => context.onOpenChange(false) }), _jsx("div", { className: cn('relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl shadow-black/10 border border-gray-200/50 dark:border-gray-700 transition-all duration-200', show ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4', className), children: children })] }));
};
const DialogHeader = ({ className, children }) => {
    const context = React.useContext(DialogContext);
    return (_jsxs("div", { className: cn('flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-6 py-4', className), children: [children, _jsx("button", { onClick: () => context?.onOpenChange(false), className: "rounded-xl p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors", children: _jsx(X, { className: "w-5 h-5" }) })] }));
};
const DialogTitle = ({ className, children }) => (_jsx("h3", { className: cn('text-lg font-semibold text-gray-900 dark:text-white', className), children: children }));
const DialogDescription = ({ className, children }) => (_jsx("p", { className: cn('text-sm text-gray-500 dark:text-gray-400', className), children: children }));
export { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription };
